// One line of values in a cell (tags, linked records, people): as many as fit whole, the rest folded into «+N» with the list
// on hover and click (owner 29.10, п.9). Widths of the values are measured once per content; a column resize only recounts.

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/primitives.tsx';
import { fitCount, hiddenCount, hiddenHint } from './fit.ts';
import s from './fields.module.css';

export interface FitItem { key: string; node: ReactNode; label: string }

/** One observer for every row of values on the screen: a thousand cells do not make a thousand observers. */
const watchers = new WeakMap<Element, () => void>();
let observer: ResizeObserver | null = null;
const observe = (el: Element, fn: () => void) => {
  observer ??= new ResizeObserver((entries) => { for (const e of entries) watchers.get(e.target)?.(); });
  watchers.set(el, fn);
  observer.observe(el);
  return () => { observer?.unobserve(el); watchers.delete(el); };
};

/** Room the badge takes before it is drawn: «+» and the digits at the meta size, at least a chip's height. */
const badgeGuess = (n: number) => Math.max(22, 14 + 7 * String(n).length);

/**
 * `items` are the values drawn in the row; `total` counts all of them when only the first are drawn (hundreds of links),
 * and `all` builds the full list for the popover only when it opens.
 */
export function FitRow({ items, total = items.length, all, wrap }: { items: FitItem[]; total?: number; all?: () => FitItem[]; wrap?: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const widths = useRef<number[] | null>(null);
  /** The badge width the last count reserved: when the drawn badge turns out wider, the row is counted again. */
  const reserved = useRef(0);
  const beyond = Math.max(0, total - items.length);
  const sig = `${items.map((i) => i.key).join('|')}#${beyond}`;
  const [state, setState] = useState({ sig: '', fit: items.length });
  const [open, setOpen] = useState(false);
  // New values (and the first ones) are drawn all at once to be measured, then folded before the paint.
  const fit = state.sig === sig ? state.fit : items.length;
  const many = !wrap && (items.length > 1 || beyond > 0);

  const recount = useRef(() => {});
  recount.current = () => {
    const el = ref.current;
    if (!el || !widths.current) return;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    const drawn = (el.querySelector('[data-more]') as HTMLElement | null)?.offsetWidth;
    const badge = Math.max(drawn ?? 0, badgeGuess(total - 1));
    reserved.current = badge;
    const n = fitCount(widths.current, el.clientWidth, gap, badge, beyond);
    setState((st) => (st.sig === sig && st.fit === n ? st : { sig, fit: n }));
  };

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !many) return;
    const kids = [...el.children].filter((c) => !(c as HTMLElement).dataset.more) as HTMLElement[];
    if (kids.length === items.length) widths.current = kids.map((k) => k.offsetWidth);
    recount.current();
    return observe(el, () => recount.current());
  }, [sig, many]);
  // The badge's own width is known only once it is drawn: a wider «+12» than reserved folds one more value.
  useLayoutEffect(() => {
    const b = ref.current?.querySelector('[data-more]') as HTMLElement | null;
    if (b && b.offsetWidth > reserved.current + 0.5) recount.current();
  });

  const hidden = many ? hiddenCount(items.length, fit, total) : beyond;
  const shown = many ? items.slice(0, fit) : items;
  const badge = hidden > 0 && (
    <span className={s.moreBox} data-more>
      <MoreButton hidden={hidden} hint={hiddenHint(items.slice(shown.length).map((i) => i.label), hidden)} onOpen={() => setOpen(true)} />
      {/* The list exists only while open: a closed popover in every cell would cost a provider per row. */}
      {open && (
        <Popover open onOpenChange={setOpen}>
          <PopoverAnchor className={s.moreAnchor} />
          <PopoverContent align="start">
            <span className={s.moreList} onClick={(e) => e.stopPropagation()}>{(all?.() ?? items).map((i) => <span key={i.key} className={s.fitItem}>{i.node}</span>)}</span>
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
  if (!many) return <span className={wrap ? s.chipsWrap : s.chips}>{shown.map((i) => <span key={i.key} className={s.fitItem}>{i.node}</span>)}{badge}</span>;
  return (
    // While measured, no value shrinks: a badge of undrawn links is there from the start and would squeeze the first one.
    <span ref={ref} className={s.fit} data-measure={state.sig !== sig || undefined}>
      {shown.map((i) => <span key={i.key} className={s.fitItem}>{i.node}</span>)}
      {badge}
    </span>
  );
}

function MoreButton({ hidden, hint, onOpen }: { hidden: number; hint: string; onOpen: () => void }) {
  return (
    <button type="button" className={s.moreBtn} title={hint || 'Показать все'} aria-label={hint ? `Ещё ${hidden}: ${hint}` : `Ещё ${hidden}`}
      onClick={(e) => { e.stopPropagation(); onOpen(); }} onDoubleClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>+{hidden}</button>
  );
}
